import { useCallback, useState } from "react"

import { dequal } from "dequal/lite"

import useDebouncedValue from "web/hooks/behaviour/useDebouncedValue"
import useSelectionBroadcast from "web/hooks/behaviour/useSelectionBroadcast"

import { ObjectMap } from "./ObjectMap"
import { ObjectTable } from "./ObjectTable"

export default function ObjectMapTableSplit({
  subjectType,
  parentSubjectType,
  parentSubjectId,
  topic,
  modalTitle,
  fetchURL,
  mapFetchURL,
  exportPath,
  onItemClick,
  onMapItemClick,
  subjectBaseUrl,
  enableScheduledExport = false,
  enableExport = true,
  isFavouriteEnabled = true,
  enableEdit = true,
  enableFieldSelection = true,
  onAdd,
  listTypeFriendlyName,
  enableSelection = false,
}) {
  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300)

  // One selection feeds both panes, and any selection visible in either pane
  // counts for bulk actions: the map spans every table page but omits subjects
  // without locations, so neither pane's dataset can stand for both.
  const [selectedIds, setSelectedIds] = useState([])
  const [tableIds, setTableIds] = useState([])
  const [mapIds, setMapIds] = useState([])

  // A pane in flight reports its ids on every render; keeping the previous
  // state reference when they are unchanged stops that re-render feedback loop.
  const handleTableIdsChanged = useCallback((ids) => {
    setTableIds((previous) => (dequal(previous, ids) ? previous : ids))
  }, [])

  const handleMapIdsChanged = useCallback((ids) => {
    setMapIds((previous) => (dequal(previous, ids) ? previous : ids))
  }, [])

  const visibleSelectedIds = selectedIds.filter(
    (id) => tableIds.includes(id) || mapIds.includes(id)
  )

  useSelectionBroadcast({ enableSelection, selectedIds: visibleSelectedIds })

  return (
    <div className="flex min-h-0 grow">
      <div className="hidden min-w-0 overflow-hidden sm:flex sm:basis-[45%]">
        <ObjectMap
          subjectType={subjectType}
          parentSubjectType={parentSubjectType}
          parentSubjectId={parentSubjectId}
          topic={topic}
          modalTitle={modalTitle}
          fetchURL={mapFetchURL}
          onItemClick={onMapItemClick}
          subjectBaseUrl={subjectBaseUrl}
          searchQuery={debouncedSearchQuery}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          onAvailableIdsChanged={handleMapIdsChanged}
          splitView
        />
      </div>
      <div className="flex w-full min-w-0 overflow-hidden pl-3 sm:w-auto sm:min-w-[400px] sm:basis-[55%] md:pl-5">
        <ObjectTable
          subjectType={subjectType}
          parentSubjectType={parentSubjectType}
          parentSubjectId={parentSubjectId}
          topic={topic}
          modalTitle={modalTitle}
          fetchURL={fetchURL}
          exportPath={exportPath}
          onItemClick={onItemClick}
          subjectBaseUrl={subjectBaseUrl}
          enableScheduledExport={enableScheduledExport}
          enableExport={enableExport}
          isFavouriteEnabled={isFavouriteEnabled}
          enableEdit={enableEdit}
          enableFieldSelection={enableFieldSelection}
          onAdd={onAdd}
          listTypeFriendlyName={listTypeFriendlyName}
          enableSelection={enableSelection}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          onAvailableIdsChanged={handleTableIdsChanged}
          splitView
        />
      </div>
    </div>
  )
}
